import type { CostImportColumn, CostImportError } from '../types'

type T = (key: string, opts?: Record<string, unknown>) => string

const KEY = /^CostControl_(error|import)_[a-z_]+$/
const firstLine = (m: string) => m.split('\n')[0].replace(/^(GraphQL error|Network error): /, '').trim()

/** True when the server's shield denied the request (the shield's own two English sentences). */
export const isPermissionError = (message: string | null | undefined): boolean => {
  const m = firstLine(message ?? '')
  return m === 'Permission denied.' || m === 'Not Authorised!'
}

/**
 * True when a record read was refused the way a deleted record is: the shield's resource loader denies a missing id,
 * and without the shield the server answers the record's own not-found key.
 */
export const isRecordGoneError = (message: string | null | undefined, notFoundKey: string): boolean =>
  isPermissionError(message) || firstLine(message ?? '') === notFoundKey

export const isContractGoneError = (message: string | null | undefined): boolean => isRecordGoneError(message, 'CostControl_error_contract_not_found')

/**
 * A GraphQL error → text for a toast. The server throws i18n keys (CostControl_error_*, CostControl_import_*);
 * the shield, guards and the Money scalar throw English sentences.
 */
export const describeCostControlError = (message: string | null | undefined, t: T): string => {
  const m = firstLine(message ?? '')
  if (KEY.test(m)) return t(m)
  if (isPermissionError(message)) return t('CostControl_error_permission')
  if (m.includes('Money must be')) return t('CostControl_error_bad_amount')
  if (m === 'Project not found or access denied') return t('CostControl_error_project_not_found')
  return m || t('Common_error')
}

/** One import row error: the translated key; the server's English message only for a key we do not know. */
export const importErrorText = (e: Pick<CostImportError, 'key' | 'message'>, t: T): string => (KEY.test(e.key) ? t(e.key) : e.message)

const IMPORT_COLUMN_KEY: Record<CostImportColumn, string> = {
  building: 'CostControl_import_col_building',
  category: 'CostControl_import_col_category',
  subcategory: 'CostControl_import_col_subcategory',
  item: 'CostControl_import_col_item',
  code: 'CostControl_import_col_code',
  originalBudget: 'CostControl_import_col_originalBudget',
  section: 'CostControl_import_col_section',
  name: 'CostControl_import_col_item',
  unit: 'CostControl_import_col_unit',
  quantity: 'CostControl_import_col_quantity',
  unitPrice: 'CostControl_import_col_unitPrice',
  amount: 'CostControl_import_col_amount',
  discountPercent: 'CostControl_import_col_discountPercent',
}

/** Column of an import error; empty for a whole-file error (row 0, column null). */
export const importColumnLabel = (column: string | null, t: T): string =>
  column && Object.prototype.hasOwnProperty.call(IMPORT_COLUMN_KEY, column) ? t(IMPORT_COLUMN_KEY[column as CostImportColumn]) : ''
